import { useState } from "react";
import type { TreeNode } from "./tree";
import { fmtBytes } from "./tree";
import { useDashboardStore, type StreamStats } from "./store";
import type { NodeStats } from "./useTreeStats";

export function TreeNav({ tree, selectedKey, onSelect, collapsed, onToggleCollapse, statsMap, liveCount, liveSelected, onSelectLive }: {
    tree: TreeNode[];
    selectedKey: string | null;
    onSelect: (key: string, type: "year" | "product" | "day" | "stream") => void;
    collapsed: boolean;
    onToggleCollapse: () => void;
    statsMap?: Map<string, NodeStats>;
    liveCount: number;
    liveSelected: boolean;
    onSelectLive: () => void;
}) {
    const [expanded, setExpanded] = useState<Set<string>>(() => {
        const s = new Set<string>();
        if (tree[0]) { s.add(tree[0].key); if (tree[0].children[0]) s.add(tree[0].children[0].key); }
        return s;
    });

    const toggle = (key: string) => {
        setExpanded(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    if (collapsed) {
        return (
            <div className="tree-nav collapsed" onClick={onToggleCollapse}>
                <div className="tree-toggle">&#9776;</div>
            </div>
        );
    }

    return (
        <div className="tree-nav">
            <div className={`tree-item ${liveSelected ? "active" : ""}`}
                onClick={onSelectLive}
                style={{ padding: "8px 10px", borderBottom: "1px solid #222", fontWeight: 600 }}>
                {liveCount > 0 ? <span className="live-dot" /> : <span style={{ color: "#444", marginRight: 4 }}>○</span>}
                <span className="tree-label">Live Now</span>
                {liveCount > 0 && <span className="tree-badge partial">{liveCount}</span>}
            </div>
            <div className="tree-header">
                <span style={{ fontWeight: 600, color: "#da4165" }}>Archive</span>
                <span className="tree-collapse" onClick={onToggleCollapse}>&laquo;</span>
            </div>
            {tree.map(yearNode => (
                <YearNode key={yearNode.key} node={yearNode} expanded={expanded} toggle={toggle}
                    selectedKey={selectedKey} onSelect={onSelect} statsMap={statsMap} />
            ))}
        </div>
    );
}

function StatsBadge({ stats }: { stats?: NodeStats }) {
    if (!stats || stats.totalChunks === 0) return null;
    return (
        <span className="tree-stats">
            <span className="tree-size">{fmtBytes(stats.totalSize)}</span>
            <span className={`tree-badge ${stats.pct >= 100 ? "complete" : stats.pct > 0 ? "partial" : ""}`}>
                {stats.pct}%
            </span>
        </span>
    );
}

function YearNode({ node, expanded, toggle, selectedKey, onSelect, statsMap }: {
    node: TreeNode; expanded: Set<string>; toggle: (k: string) => void;
    selectedKey: string | null; onSelect: (key: string, type: any) => void;
    statsMap?: Map<string, NodeStats>;
}) {
    const isOpen = expanded.has(node.key);
    const stats = statsMap?.get(node.key);
    return (
        <div>
            <div className={`tree-item tree-year ${selectedKey === node.key ? "active" : ""}`}
                onClick={() => { toggle(node.key); onSelect(node.key, "year"); }}>
                <span className="tree-arrow">{isOpen ? "▼" : "▶"}</span>
                <span className="tree-label">{node.label}</span>
                <StatsBadge stats={stats} />
            </div>
            {isOpen && node.children.map(prodNode => (
                <ProductNode key={prodNode.key} node={prodNode} expanded={expanded} toggle={toggle}
                    selectedKey={selectedKey} onSelect={onSelect} statsMap={statsMap} />
            ))}
        </div>
    );
}

function ProductNode({ node, expanded, toggle, selectedKey, onSelect, statsMap }: {
    node: TreeNode; expanded: Set<string>; toggle: (k: string) => void;
    selectedKey: string | null; onSelect: (key: string, type: any) => void;
    statsMap?: Map<string, NodeStats>;
}) {
    const isOpen = expanded.has(node.key);
    const stats = statsMap?.get(node.key);
    return (
        <div>
            <div className={`tree-item tree-product ${selectedKey === node.key ? "active" : ""}`}
                onClick={() => { toggle(node.key); onSelect(node.key, "product"); }}>
                <span className="tree-arrow">{isOpen ? "▼" : "▶"}</span>
                <span className="tree-label">{node.label}</span>
                <StatsBadge stats={stats} />
            </div>
            {isOpen && node.children.map(dayNode => (
                <DayNode key={dayNode.key} node={dayNode} expanded={expanded} toggle={toggle}
                    selectedKey={selectedKey} onSelect={onSelect} statsMap={statsMap} />
            ))}
        </div>
    );
}

function DayNode({ node, expanded, toggle, selectedKey, onSelect, statsMap }: {
    node: TreeNode; expanded: Set<string>; toggle: (k: string) => void;
    selectedKey: string | null; onSelect: (key: string, type: any) => void;
    statsMap?: Map<string, NodeStats>;
}) {
    const isOpen = expanded.has(node.key);
    const stats = statsMap?.get(node.key);
    return (
        <div>
            <div className={`tree-item tree-day ${selectedKey === node.key ? "active" : ""}`}
                onClick={() => { toggle(node.key); onSelect(node.key, "day"); }}>
                <span className="tree-arrow">{node.children.length > 0 ? (isOpen ? "▼" : "▶") : "·"}</span>
                <span className="tree-label">{node.label}</span>
                <StatsBadge stats={stats} />
            </div>
            {isOpen && node.children.map(child =>
                child.children.length > 0
                    ? <EventGroupNode key={child.key} node={child} expanded={expanded} toggle={toggle}
                        selectedKey={selectedKey} onSelect={onSelect} />
                    : <StageLeaf key={child.key} node={child} selectedKey={selectedKey} onSelect={onSelect} />
            )}
        </div>
    );
}

function EventGroupNode({ node, expanded, toggle, selectedKey, onSelect }: {
    node: TreeNode; expanded: Set<string>; toggle: (k: string) => void;
    selectedKey: string | null; onSelect: (key: string, type: any) => void;
}) {
    const isOpen = expanded.has(node.key);
    return (
        <div>
            <div className={`tree-item tree-event ${selectedKey === node.key ? "active" : ""}`}
                onClick={() => { toggle(node.key); onSelect(node.key, "stream"); }}>
                <span className="tree-arrow">{isOpen ? "▼" : "▶"}</span>
                <span className="tree-label">{node.label}</span>
                <span className="tree-badge">{node.children.length}</span>
            </div>
            {isOpen && <div className="tree-event-children">
                {node.children.map(variant => (
                    <StageLeaf key={variant.key} node={variant} selectedKey={selectedKey} onSelect={onSelect} />
                ))}
            </div>}
        </div>
    );
}

function StageLeaf({ node, selectedKey, onSelect }: {
    node: TreeNode; selectedKey: string | null; onSelect: (key: string, type: any) => void;
}) {
    const s = node.streams?.[0];
    // Hooks must run unconditionally — select before the early return below
    const vs = useDashboardStore(st => (s ? st.stats[s.stream_key] : undefined));
    if (!s) return null;
    const isActive = s.status === "active";
    const stored = vs?.stored || 0;
    const total = vs ? vs.pending + vs.downloading + vs.stored + vs.failed + vs.expired : 0;
    const pct = total > 0 ? Math.round(stored / total * 100) : 0;

    return (
        <div className={`tree-item tree-stage ${selectedKey === node.key ? "active" : ""}`}
            onClick={() => onSelect(node.key, "stream")}>
            {isActive && <span className="live-dot" style={{ width: 6, height: 6 }} />}
            <span className="tree-label">{node.label}</span>
            {total > 0 && <span className={`tree-badge ${pct >= 100 ? "complete" : pct > 0 ? "partial" : ""}`}>
                {pct}%
            </span>}
        </div>
    );
}
